import { useEffect, useRef } from 'react';
import { fetchNSCorrespondents } from '../api/north-standard/correspondents';
import { getIndustryNews } from '../api/north-standard/industry-news';
import {
  getIncidents,
  getMaritimeAreas,
} from '../api/risk-intelligence/incidents';
import { getPorts } from '../api/risk-intelligence/ports';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  defaultIncidentFilters,
  setIncidentFilters,
} from '../maritime-menu-options/incidents-panel/incident-panel.slice';
import { setAreas } from '../maritime-menu-options/ri-maritime-areas-panel/ri-maritime-areas.slice';
import { setCorrespondents } from '../state/correspondents/correspondents.slice';
import { BoundaryPoint } from '../state/feature-flags/feature-flags.slice';
import { setIncidents } from '../state/incidents/incidents.slice';
import {
  IRAN_CONFLICT_AREA_TITLES,
  setIranConflictPolygon,
  setFocusRegionStartDate,
} from '../state/focus-region/focus-region.slice';
import { setIndustryNews } from '../state/industry-news/industry-news.slice';
import store from '../store';
import GeoHelper from '../utils/geo-helpers.utils';
import MapLayer, { MapGroupLayers } from './map-layer-manager/map-layer.enum';
import MapHelpers from './map.utils';

/**
 * Earliest date shown when conflict mode is active.
 * Hardcoded to Jan 1st 2026 — the year the Iran Conflict feature launched.
 * Update this constant when the conflict scope changes.
 */
const FOCUS_REGION_START_DATE = '2026-01-01';

/** Convert the backend [{lon, lat}] array to a closed GeoJSON Polygon */
function boundaryPointsToPolygon(points: BoundaryPoint[]): GeoJSON.Polygon {
  const coords = points.map(
    ({ lon, lat }) => [Number(lon), Number(lat)] as [number, number]
  );
  // Close the ring if not already closed
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push(first);
  }
  return { type: 'Polygon', coordinates: [coords] };
}

/** Derive a LngLatBoundsLike bbox tuple from a polygon's coordinate ring */
function polygonToBBox(
  polygon: GeoJSON.Polygon
): [number, number, number, number] {
  const ring = polygon.coordinates[0];
  const lons = ring.map(([lon]) => lon);
  const lats = ring.map(([, lat]) => lat);
  return [
    Math.min(...lons),
    Math.min(...lats),
    Math.max(...lons),
    Math.max(...lats),
  ];
}

/** Layers we show when conflict mode activates (those not already visible). */
const CONFLICT_ACTIVATED_LAYERS = [
  ...MapGroupLayers.INCIDENTS,
  ...MapGroupLayers.PORTS,
  ...MapGroupLayers.CORRESPONDENTS,
  MapLayer.RI_MARITIME_AREAS,
  MapLayer.INDUSTRY_NEWS,
  MapLayer.INDUSTRY_NEWS_CLUSTERS,
  MapLayer.INDUSTRY_NEWS_CLUSTER_COUNT,
];

/**
 * Reactive hook that watches the iranConflict.active Redux flag and applies or
 * removes all map-side effects (zoom, layer visibility, filters) without
 * requiring the Iran Conflict panel component to manage cleanup itself.
 *
 * The conflict boundary is read from the feature flag so the backend controls
 * the exact polygon. Falls back to the hardcoded constants if not yet available.
 *
 * Call this hook once inside the Map component (after map initialisation).
 */
function useIranConflictMapEffects() {
  const dispatch = useAppDispatch();
  const mapInitialised = useAppSelector((state) => state.map.mapInitialised);
  const focusRegionActive = useAppSelector((state) => state.focusRegion.active);
  const contentBoundary = useAppSelector(
    (state) =>
      state.featureFlags.flags.canAccessFeatureIranConflictPanel.contentBoundary
  );
  const initialViewBoundary = useAppSelector(
    (state) =>
      state.featureFlags.flags.canAccessFeatureIranConflictPanel
        .initialViewBoundary
  );

  // Record pre-activation visibility so we can restore it precisely on exit.
  const preActivationVisibilityRef = useRef<Map<MapLayer, boolean>>(new Map());
  // Fly to the region only on initial activation — not on boundary re-runs.
  const hasFlewRef = useRef(false);

  useEffect(() => {
    if (!mapInitialised) return;

    const clusterLayers = [
      MapLayer.INCIDENT_CLUSTERS,
      MapLayer.INCIDENT_CLUSTER_COUNT,
      MapLayer.PORT_CLUSTERS,
      MapLayer.PORT_CLUSTER_COUNT,
      MapLayer.FIRST_CALL_CLUSTERS,
      MapLayer.FIRST_CALL_CLUSTER_COUNT,
      MapLayer.CORRESPONDENT_CLUSTERS,
      MapLayer.CORRESPONDENT_CLUSTER_COUNT,
    ];
    const pointLayers = [
      MapLayer.INCIDENTS,
      MapLayer.PORTS,
      MapLayer.FIRST_CALL_PORTS,
      MapLayer.CORRESPONDENTS,
    ];

    if (focusRegionActive) {
      // contentBoundary is required for spatial filtering; initialViewBoundary is
      // optional and falls back to contentBoundary for the flyTo viewport calculation.
      if (!contentBoundary || contentBoundary.length < 3) return;

      const conflictPolygon = boundaryPointsToPolygon(contentBoundary);
      const viewPoints =
        initialViewBoundary && initialViewBoundary.length >= 3
          ? initialViewBoundary
          : contentBoundary;
      const conflictBBox = polygonToBBox(boundaryPointsToPolygon(viewPoints));

      // Store the resolved polygon and start date in Redux so panels can use them for filtering
      dispatch(setIranConflictPolygon(conflictPolygon));
      dispatch(setFocusRegionStartDate(FOCUS_REGION_START_DATE));

      // 1. Update the border layer source with the resolved polygon
      GeoHelper.setMapboxGeoJSONSourceData(MapLayer.FOCUS_REGION_AREA, {
        type: 'Feature',
        geometry: conflictPolygon,
        properties: {},
      });

      // 2. Apply all spatial filters BEFORE making layers visible so data already
      //    in Redux never briefly shows unfiltered.
      //    Cluster layers carry an existing ['has','point_count'] base filter —
      //    we must preserve it with ['all', ...] or individual point features
      //    will be evaluated by the cluster circle paint and throw null errors.
      const withinFilter = ['within', conflictPolygon];

      clusterLayers.forEach((layer) => {
        if (MapHelpers.getLayer(layer)) {
          MapHelpers.setFilter(layer, [
            'all',
            ['has', 'point_count'],
            withinFilter,
          ]);
        }
      });

      pointLayers.forEach((layer) => {
        if (MapHelpers.getLayer(layer)) {
          MapHelpers.setFilter(layer, [
            'all',
            ['!', ['has', 'point_count']],
            withinFilter,
          ]);
        }
      });

      // Industry news is filtered at data level (map.tsx) — no within expression
      // needed here, which avoids queryRenderedFeatures evaluation errors.
      if (MapHelpers.getLayer(MapLayer.INDUSTRY_NEWS)) {
        MapHelpers.setFilter(MapLayer.INDUSTRY_NEWS, [
          '!',
          ['has', 'point_count'],
        ]);
      }
      if (MapHelpers.getLayer(MapLayer.INDUSTRY_NEWS_CLUSTERS)) {
        MapHelpers.setFilter(MapLayer.INDUSTRY_NEWS_CLUSTERS, [
          'has',
          'point_count',
        ]);
      }
      if (MapHelpers.getLayer(MapLayer.INDUSTRY_NEWS_CLUSTER_COUNT)) {
        MapHelpers.setFilter(MapLayer.INDUSTRY_NEWS_CLUSTER_COUNT, [
          'has',
          'point_count',
        ]);
      }

      const areaFilter = [
        'any',
        ...IRAN_CONFLICT_AREA_TITLES.map((name) => [
          'all',
          ['has', 'title'],
          [
            '>',
            ['index-of', name.toLowerCase(), ['downcase', ['get', 'title']]],
            -1,
          ],
        ]),
      ];
      if (MapHelpers.getLayer(MapLayer.RI_MARITIME_AREAS)) {
        MapHelpers.setFilter(MapLayer.RI_MARITIME_AREAS, areaFilter);
      }

      // Scope incidents to the conflict polygon and limit to this year via Redux filter (used for panel list + API refetch)
      dispatch(
        setIncidentFilters({
          geo: conflictPolygon,
          startDate: FOCUS_REGION_START_DATE,
        })
      );

      // 3. Fly to the conflict region on first activation only.
      //    cameraForBounds computes center+zoom geometrically (no setPadding
      //    interaction) so the known Mapbox padding-accumulation bug is avoided.
      if (!hasFlewRef.current) {
        hasFlewRef.current = true;
        const mapInstance = MapHelpers.getMapInstance();
        const menuContainer = document
          .querySelector('.App')
          ?.querySelector('.menu-container');
        const fullMenuWidth =
          (
            menuContainer?.children[0] as HTMLElement | undefined
          )?.getBoundingClientRect().width ?? 0;
        const basePadding = window.innerWidth * 0.1;
        const camera = mapInstance?.cameraForBounds(
          [
            [conflictBBox[0], conflictBBox[1]],
            [conflictBBox[2], conflictBBox[3]],
          ],
          {
            padding: {
              top: basePadding,
              right: basePadding,
              bottom: basePadding,
              left: basePadding + fullMenuWidth,
            },
          }
        );
        if (camera) {
          MapHelpers.flyTo({
            center: [camera.center.lng, camera.center.lat],
            zoom: camera.zoom,
            curve: 1.4,
            speed: 0.8,
            animate: true,
          });
        }
      }

      // 4. Show the blue border overlay layers
      MapHelpers.setLayerVisibility(MapLayer.FOCUS_REGION_AREA, true);
      MapHelpers.setLayerVisibility(MapLayer.FOCUS_REGION_AREA_FILL, true);

      // 5. Make conflict-relevant layers visible now that filters are in place.
      // Snapshot pre-activation visibility so we can restore it precisely on exit.
      //
      // Problem: Incidents, Ports, and Industry News layers are VISIBLE by
      // default in the map style even on fresh load (before the user opens those
      // panels). On fresh load they look "off" because their sources are empty.
      // Iran Conflict fetches data into those sources, then on deactivation the
      // raw isLayerVisible() snapshot would restore them to "visible" — but now
      // they show globally-unfiltered data rather than nothing.
      //
      // Fix: treat a default-visible layer as "not user-enabled" unless its
      // backing dataset was already populated in Redux BEFORE this activation.
      const preActState = store.getState();
      const dataPreloaded = {
        incidents: preActState.incidents.incidents !== null,
        ports: preActState.ports.ports !== null,
        industryNews: preActState.industryNews.industryNews !== null,
      };

      const snapshot = new Map<MapLayer, boolean>();
      CONFLICT_ACTIVATED_LAYERS.forEach((layer) => {
        if (!MapHelpers.getLayer(layer)) return;
        const mapVisible = MapHelpers.isLayerVisible(layer);

        // For layers visible by default, only keep them visible after
        // deactivation if the user had already loaded data into them.
        let wasUserEnabled = mapVisible;
        if (mapVisible) {
          if (MapGroupLayers.INCIDENTS.includes(layer as never)) {
            wasUserEnabled = dataPreloaded.incidents;
          } else if (MapGroupLayers.PORTS.includes(layer as never)) {
            wasUserEnabled = dataPreloaded.ports;
          } else if (
            [
              MapLayer.INDUSTRY_NEWS,
              MapLayer.INDUSTRY_NEWS_CLUSTERS,
              MapLayer.INDUSTRY_NEWS_CLUSTER_COUNT,
            ].includes(layer)
          ) {
            wasUserEnabled = dataPreloaded.industryNews;
          }
        }

        snapshot.set(layer, wasUserEnabled);
        MapHelpers.setLayerVisibility(layer, true);
      });
      preActivationVisibilityRef.current = snapshot;

      // 6. Ensure all conflict-relevant data is loaded.
      // map.tsx has reactive effects that populate Mapbox sources when Redux state changes,
      // so we just need to trigger the API calls if data isn't already present.
      const state = store.getState();

      if (!state.ports.ports || state.ports.ports.length === 0) {
        getPorts(state.user.idToken, state.featureFlags.flags).catch(() => {});
      }

      if (!state.correspondents.correspondents) {
        fetchNSCorrespondents()
          .then((response) => {
            response.data.sort((a, b) =>
              a.port_information.name.localeCompare(b.port_information.name)
            );
            store.dispatch(setCorrespondents(response.data));
          })
          .catch(() => {});
      }

      if (!state.incidents.incidents) {
        getIncidents({ filters: state.incidentsPanel.filters })
          .then((incidents) => store.dispatch(setIncidents(incidents)))
          .catch(() => {});
      }

      if (!state.industryNews.industryNews) {
        getIndustryNews()
          .then((news) => store.dispatch(setIndustryNews(news)))
          .catch(() => {});
      }

      if (
        !state.riMaritimeAreas.areas ||
        state.riMaritimeAreas.areas.length === 0
      ) {
        getMaritimeAreas()
          .then((areas) => store.dispatch(setAreas(areas)))
          .catch(() => {});
      }
    } else {
      // Clear the stored polygon
      dispatch(setIranConflictPolygon(null));

      // Hide border overlay
      if (MapHelpers.getLayer(MapLayer.FOCUS_REGION_AREA)) {
        MapHelpers.setLayerVisibility(MapLayer.FOCUS_REGION_AREA, false);
      }
      if (MapHelpers.getLayer(MapLayer.FOCUS_REGION_AREA_FILL)) {
        MapHelpers.setLayerVisibility(MapLayer.FOCUS_REGION_AREA_FILL, false);
      }

      // Restore each layer to the visibility it had before conflict mode activated.
      preActivationVisibilityRef.current.forEach((wasVisible, layer) => {
        if (MapHelpers.getLayer(layer)) {
          MapHelpers.setLayerVisibility(layer, wasVisible);
        }
      });
      preActivationVisibilityRef.current = new Map();
      hasFlewRef.current = false;

      // Restore original base filters — cluster layers need ['has','point_count']
      // and point layers need ['!', ['has','point_count']] to keep them separated.
      clusterLayers.forEach((layer) => {
        if (MapHelpers.getLayer(layer)) {
          MapHelpers.setFilter(layer, ['has', 'point_count']);
        }
      });
      pointLayers.forEach((layer) => {
        if (MapHelpers.getLayer(layer)) {
          MapHelpers.setFilter(layer, ['!', ['has', 'point_count']]);
        }
      });

      // Remove maritime areas filter
      if (MapHelpers.getLayer(MapLayer.RI_MARITIME_AREAS)) {
        MapHelpers.setFilter(MapLayer.RI_MARITIME_AREAS, undefined);
      }

      // Clear incident geo filter and restore default date range
      dispatch(
        setIncidentFilters({
          geo: null,
          startDate: defaultIncidentFilters.startDate,
        })
      );
    }
  }, [focusRegionActive, mapInitialised, contentBoundary, initialViewBoundary]);
}

export default useIranConflictMapEffects;
